/**
 * 授权码 + PKCE（RFC 7636）素材与待处理态（附录D §1、附录E 前端篇 §6.1）。
 *
 * <p>code_verifier 只在本地生成、绝不进网络请求以外的任何地方；之所以落到 sessionStorage，
 * 是因为授权码流程要整页跳到中心登录再跳回，页面上下文会丢失。回调页一旦读取即清除，
 * 保证一个 verifier 只能兑换一次。
 */

const VERIFIER_KEY = 'uc.pkce.verifier'
const STATE_KEY = 'uc.pkce.state'
const REDIRECT_KEY = 'uc.pkce.redirect'
const CLIENT_KEY = 'uc.pkce.client'
const TARGET_KEY = 'uc.pkce.target'

export interface PendingAuthorization {
  verifier: string
  challenge: string
  state: string
  /** 发起授权的客户端：uc-me（个人中心）/ uc-admin（管理后台） */
  clientId: string
  /** 授权完成后要回到的前端路径 */
  redirect: string
}

function base64Url(source: ArrayBuffer | Uint8Array): string {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomToken(bytes: number): string {
  const buffer = new Uint8Array(bytes)
  crypto.getRandomValues(buffer)
  return base64Url(buffer)
}

/**
 * 由 verifier 派生 S256 challenge（RFC 7636 §4.2）。
 *
 * <p>待处理态只持久化 verifier —— challenge 是它的哈希，随时可重算，
 * 再落一份 sessionStorage 就多一处可能与 verifier 不一致的状态。
 * 因此从 storage 读回的待处理态**必须**用这里重新派生 challenge，不能直接拿着字段用。
 */
export async function deriveChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(digest)
}

/** 生成 verifier / challenge(S256) / state 三件套。 */
export async function createPendingAuthorization(
  redirect: string,
  clientId: string,
): Promise<PendingAuthorization> {
  const verifier = randomToken(32)
  return { verifier, challenge: await deriveChallenge(verifier), state: randomToken(16), clientId, redirect }
}

/** 记录发起端（回调失败重试时仍要回到原端），登录成功后由回调页清除。 */
export function saveTarget(target: 'me' | 'admin'): void {
  sessionStorage.setItem(TARGET_KEY, target)
}

export function peekTarget(): 'me' | 'admin' {
  return sessionStorage.getItem(TARGET_KEY) === 'admin' ? 'admin' : 'me'
}

export function clearTarget(): void {
  sessionStorage.removeItem(TARGET_KEY)
}

export function savePending(pending: PendingAuthorization): void {
  sessionStorage.setItem(VERIFIER_KEY, pending.verifier)
  sessionStorage.setItem(STATE_KEY, pending.state)
  sessionStorage.setItem(REDIRECT_KEY, pending.redirect)
  sessionStorage.setItem(CLIENT_KEY, pending.clientId)
}

/** 读取并立即清除（verifier 一次性使用）。 */
export function takePending(): PendingAuthorization | null {
  const pending = peekPending()
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(REDIRECT_KEY)
  sessionStorage.removeItem(CLIENT_KEY)
  return pending
}

/**
 * 只读不消费：登录页判断"是否处于授权码流程中"用，素材仍留给回调页兑取。
 *
 * <p>返回的 {@code challenge} 恒为空串：storage 里只留 verifier，
 * 需要用它的地方（重新发起授权）自行调 {@link deriveChallenge} 派生，
 * 切勿把这个字段直接拼进 authorize 请求 —— 空的 code_challenge 会被中心判 invalid_request。
 */
export function peekPending(): PendingAuthorization | null {
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const state = sessionStorage.getItem(STATE_KEY)
  const redirect = sessionStorage.getItem(REDIRECT_KEY)
  const clientId = sessionStorage.getItem(CLIENT_KEY)
  if (verifier === null || state === null || clientId === null) {
    return null
  }
  return { verifier, challenge: '', state, clientId, redirect: redirect ?? '/me/profile' }
}
